import { Link, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button.tsx';
import { reportClientError } from '@/lib/client-errors.ts';
import { errorMessage } from '@/lib/utils.ts';
import { useDocumentTitle } from './PageHeader.tsx';

/** Shown when a screen crashes. The error is reported to the server automatically. */
export function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  useDocumentTitle('Something went wrong');
  useEffect(() => {
    reportClientError(error);
  }, [error]);
  return (
    <div role="alert" className="mx-auto grid max-w-xl gap-4 px-4 py-16">
      <p className="type-eyebrow">Something went wrong</p>
      <h1 className="type-page m-0">This screen hit a snag</h1>
      <p className="text-ink-2">
        It’s been reported automatically. Try again — if it keeps happening, tell the owner what you
        were doing. Anything waiting to sync is still saved on this device.
      </p>
      <pre className="overflow-x-auto rounded-panel border border-line bg-surface-2 p-3 text-label text-ink-2">
        {errorMessage(error)}
      </pre>
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => void router.invalidate()}>Try again</Button>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          Reload the app
        </Button>
      </div>
    </div>
  );
}

export function NotFound() {
  useDocumentTitle('Page not found');
  return (
    <div className="mx-auto grid max-w-xl gap-4 px-4 py-16">
      <p className="type-eyebrow">404</p>
      <h1 className="type-page m-0">No page here</h1>
      <p className="text-ink-2">That address doesn’t match anything in the planner.</p>
      <div>
        <Button asChild>
          <Link to="/">Go to the dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
